import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProviderPageLayout from '../../components/ProviderPageLayout';
import { PasswordField } from '../../components/ui/PasswordField';
import { useAuth } from '../../auth/useAuth';

const ProviderReset = () => {
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState('');

  const navigate = useNavigate();
  const { confirmPassword: confirmPasswordReset } = useAuth();

  useEffect(() => {
    const resetUsername = sessionStorage.getItem('resetUsername');
    if (resetUsername) setUsername(resetUsername);
    else navigate('/provider/forgot');
  }, [navigate]);

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (!code || code.length < 6) {
      setError('Enter the 6-digit code from your email.');
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(username, code, newPassword);
      setSuccess('Password updated. Redirecting to sign in…');
      sessionStorage.removeItem('resetUsername');
      setTimeout(() => navigate('/provider/login'), 2000);
    } catch (err) {
      if (err.code === 'CodeMismatchException') {
        setError('Invalid code. Check your email.');
      } else if (err.code === 'ExpiredCodeException') {
        setError('Code expired. Request a new one.');
      } else {
        setError(err.message || 'Reset failed. Please try again.');
      }
      setLoading(false);
    }
  };

  return (
    <ProviderPageLayout
      variant="split"
      splitImage="/images/gallery-billing.png"
      splitEyebrow="Secure your account"
      splitQuote="A fresh password, and you're set. Your data stays protected, always."
    >
      <div className="provider-card provider-card--auth">
        <p className="provider-eyebrow">New password</p>
        <h1 className="provider-title">Choose a password</h1>
        <p className="provider-subtitle">
          {username ? `Enter the code sent to ${username}` : 'Enter your reset code and new password'}
        </p>

        {error && <div className="provider-alert provider-alert--error">{error}</div>}
        {success && <div className="provider-alert provider-alert--success">{success}</div>}

        <form onSubmit={handleReset}>
          <div className="provider-form-group">
            <label htmlFor="code" className="provider-label">Reset code</label>
            <input
              type="text"
              id="code"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              className="provider-input provider-input--code"
              placeholder="000000"
              maxLength={6}
            />
          </div>

          <PasswordField
            id="newPassword"
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            showRules
            autoComplete="new-password"
          />

          <PasswordField
            id="confirmPassword"
            label="Confirm password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            hint={
              confirmPassword
                ? newPassword === confirmPassword
                  ? 'Passwords match'
                  : 'Passwords do not match'
                : null
            }
          />

          <button type="submit" disabled={loading} className="provider-btn provider-btn--primary">
            {loading ? 'Updating…' : 'Update password'}
          </button>
        </form>

        <div className="provider-footer-links">
          <button type="button" className="provider-link" onClick={() => navigate('/provider/forgot')}>
            Request a new code
          </button>
        </div>
      </div>
    </ProviderPageLayout>
  );
};

export default ProviderReset;
